import type { ModelPayloadItem } from '@orthly/dentin';
import type {
    FragmentType,
    OrderDesignPreviewDesignModelPayloadsScanExport_FragmentFragmentDoc,
} from '@orthly/graphql-inline-react';
import {
    graphql,
    getFragmentData,
    PatientDetailMainScanExport_FragmentFragmentDoc,
} from '@orthly/graphql-inline-react';
import {
    getScanPayloadItems,
    useDesignModelPayloadsForScanInlineGraphql,
    ShareScansWithPatientModal,
} from '@orthly/veneer';
import _ from 'lodash';
import React from 'react';

const PatientDetailShareWithPatientModalPatient_Fragment = graphql(`
    fragment PatientDetailShareWithPatientModalPatient_Fragment on PatientDTO {
        id
    }
`);

interface PatientDetailShareWithPatientBaseProps {
    patientFragment: FragmentType<typeof PatientDetailShareWithPatientModalPatient_Fragment>;
    selectedScanExportId: string | null;
    shareWithPatientModalOpen: boolean;
    setShareWithPatientModalOpen: (open: boolean) => void;
}

interface PatientDetailShareWithPatientInnerProps extends PatientDetailShareWithPatientBaseProps {
    scanExport: FragmentType<typeof OrderDesignPreviewDesignModelPayloadsScanExport_FragmentFragmentDoc>;
}

const PatientDetailShareWithPatientInner: React.VFC<PatientDetailShareWithPatientInnerProps> = props => {
    const {
        patientFragment,
        scanExport,
        selectedScanExportId,
        shareWithPatientModalOpen,
        setShareWithPatientModalOpen,
    } = props;

    const patient = getFragmentData(PatientDetailShareWithPatientModalPatient_Fragment, patientFragment);

    const { result: designPayloads } = useDesignModelPayloadsForScanInlineGraphql(scanExport);

    const modelPayloadItems = React.useMemo<ModelPayloadItem[]>(() => {
        const itemsPotentiallyWithBite = getScanPayloadItems(_.compact(designPayloads) ?? []);
        const itemsWithoutBite = itemsPotentiallyWithBite.filter(item => !item.name.includes('Bite'));
        const jaws = _.groupBy(itemsWithoutBite, item => item.jaw);

        // If there are multiple scans per jaw, only return prepped scans.
        return Object.values(jaws).reduce((acc, items) => {
            return acc.concat(items.filter(item => !item.name.includes('PrePrep')));
        }, [] as ModelPayloadItem[]);
    }, [designPayloads]);

    const upperModel = modelPayloadItems.find(item => item.name.includes('Upper'));
    const lowerModel = modelPayloadItems.find(item => item.name.includes('Lower'));

    return (
        <ShareScansWithPatientModal
            disableAnnotations
            open={shareWithPatientModalOpen}
            setOpen={setShareWithPatientModalOpen}
            patientId={patient.id}
            scanExportId={selectedScanExportId}
            annotationUrls={[]}
            models={{ upperModel, lowerModel }}
        />
    );
};

interface PatientDetailShareWithPatientModalProps extends PatientDetailShareWithPatientBaseProps {
    selectedScanExportFragment: FragmentType<typeof PatientDetailMainScanExport_FragmentFragmentDoc> | undefined;
}

export const PatientDetailShareWithPatientModal: React.VFC<PatientDetailShareWithPatientModalProps> = props => {
    const { selectedScanExportFragment, ...others } = props;

    if (!selectedScanExportFragment) {
        return null;
    }

    const scanExport = getFragmentData(PatientDetailMainScanExport_FragmentFragmentDoc, selectedScanExportFragment);

    return <PatientDetailShareWithPatientInner scanExport={scanExport} {...others} />;
};
